/**
 * Quasi perfetta Cart Utilities
 * 
 * Handles:
 * - Free Shipping Progress Bar (calculation and updates)
 * - Cart Drawer Upsells (toggle add/remove, sync state)
 * 
 * Architecture:
 * - Global namespace: window.PulseCart
 * - Event-driven: Listens to cart updates and dispatches events
 * - Mobile-first: Optimized for performance
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION & CONSTANTS
  // ============================================================================

  const SELECTORS = {
    freeShip: '[data-pulse-free-ship]',
    freeShipBar: '[data-pulse-free-ship-track]',
    freeShipFill: '[data-pulse-free-ship-fill]',
    freeShipMsg: '[data-pulse-free-ship-msg]',
    upsellContainer: '[data-pulse-order-bump-upsells][data-context="drawer"]',
    upsellRow: '[data-pulse-upsell-row]',
    upsellToggle: '[data-upsell-toggle]',
    collectionUpsellAtc: '[data-cart-drawer-upsell-atc]'
  };

  const EVENTS = {
    cartUpdated: 'pulse:cart:updated',
    cartAdded: 'pulse:cart:added',
    cartChanged: 'pulse:cart:changed'
  };

  // ============================================================================
  // FREE SHIPPING PROGRESS BAR
  // ============================================================================

  const FreeShippingBar = {
    /**
     * Update free shipping progress bar based on cart subtotal
     * @param {Object} cart - Cart object from /cart.js
     */
    update(cart) {
      const shipEl = document.querySelector(SELECTORS.freeShip);
      if (!shipEl) return;

      // Get threshold from settings (stored in data attribute or use default)
      const thresholdCents = parseInt(shipEl.dataset.thresholdCents || shipEl.dataset.threshold || '10000', 10);
      const subtotalCents = cart.items_subtotal_price || 0;
      const remaining = Math.max(0, thresholdCents - subtotalCents);
      
      // Calculate percent (0-100)
      const percent = Math.max(0, Math.min(100, Math.round((subtotalCents / thresholdCents) * 100)));
      
      // Badge position is clamped to prevent overflow (6-94% range)
      // Clamp to 6-94% to keep badge visible within track
      const badgePercent = Math.max(6, Math.min(94, percent));
      // Special case: if at 100%, limit badge to ~97% to prevent it from being cut off
      // (badge is 34px wide, so center at 97% keeps it fully visible)
      const finalBadgePercent = percent >= 100 ? 97 : badgePercent;

      // Fill should extend to badge position, but when at 100%, limit fill to ~94% 
      // to prevent it from covering the badge (badge is 34px wide, needs space)
      let fillWidth;
      if (percent >= 100) {
        // At 100%, limit fill to 94% so badge remains visible
        fillWidth = 94;
      } else {
        // Otherwise, fill extends to badge position
        fillWidth = finalBadgePercent;
      }

      // Update CSS variables for fill and badge position
      const trackEl = shipEl.querySelector(SELECTORS.freeShipBar || '.pulse-free-ship__track');
      const fillEl = shipEl.querySelector(SELECTORS.freeShipFill || '.pulse-free-ship__fill');
      const wrapEl = shipEl.querySelector('.pulse-free-ship__wrap');
      const badgeEl = shipEl.querySelector('.pulse-free-ship__badge');
      
      if (wrapEl) {
        wrapEl.style.setProperty('--fill', fillWidth);
      }
      if (trackEl) {
        trackEl.style.setProperty('--fill', fillWidth);
        trackEl.setAttribute('aria-valuenow', percent);
      }
      if (fillEl) {
        fillEl.style.setProperty('--fill', fillWidth);
      }
      
      // Set --badge on wrap (badge element will use it for positioning)
      if (wrapEl) {
        wrapEl.style.setProperty('--badge', finalBadgePercent);
      }
      
      // Also set on badge element for redundancy
      if (badgeEl) {
        badgeEl.style.setProperty('--badge', finalBadgePercent);
      }

      // Update message
      const messageEl = shipEl.querySelector(SELECTORS.freeShipMsg);
      if (messageEl) {
        let message = '';
        if (percent < 100) {
          const amount = this.formatMoney(remaining);
          message = `You need ${amount} more to get Free Shipping`;
        } else {
          message = 'Congrats! You get FREE shipping!';
        }
        messageEl.textContent = message;
      }
    },

    /**
     * Format money in cents to currency string
     * @param {number} cents - Amount in cents
     * @returns {string} Formatted money string
     */
    formatMoney(cents) {
      if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      // Fallback: simple formatting
      const amount = (cents / 100).toFixed(2);
      return `€${amount}`;
    }
  };

  // ============================================================================
  // CART DRAWER PARTIAL UPDATE
  // ============================================================================

  const CartDrawerPartialUpdate = {
    targets: [
      { target: '#CartDrawer .drawer__cart-items-wrapper', source: '.drawer__cart-items-wrapper', useOuterHTML: true },
      { target: '#CartDrawer .cart-drawer__footer', source: '.cart-drawer__footer' }
    ],

    parseSection(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    },

    /**
     * Update only cart line items and footer without re-rendering the full drawer.
     * Keeps upsells, header and drawer state intact.
     */
    apply(sections, itemCount) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || !sections || !sections['cart-drawer']) return;

      const doc = this.parseSection(sections['cart-drawer']);

      this.targets.forEach(({ target, source, useOuterHTML }) => {
        const newEl = doc.querySelector(source);
        const currentEl = document.querySelector(target);
        if (!newEl) return;

        if (currentEl) {
          if (useOuterHTML) {
            currentEl.outerHTML = newEl.outerHTML;
          } else {
            currentEl.innerHTML = newEl.innerHTML;
          }
          return;
        }

        if (source === '.drawer__cart-items-wrapper') {
          const cartItemsContainer = document.querySelector('#CartDrawer-CartItems');
          const freeShip = document.querySelector('#CartDrawer .pulse-free-ship');
          if (!cartItemsContainer) return;

          if (freeShip) {
            freeShip.insertAdjacentHTML('afterend', newEl.outerHTML);
          } else {
            cartItemsContainer.insertAdjacentHTML('afterbegin', newEl.outerHTML);
          }
        }
      });

      const newCheckout = doc.querySelector('#CartDrawer-Checkout');
      const currentCheckout = document.querySelector('#CartDrawer-Checkout');
      if (newCheckout && currentCheckout) {
        currentCheckout.disabled = newCheckout.hasAttribute('disabled');
      }

      if (typeof itemCount === 'number') {
        cartDrawer.classList.toggle('is-empty', itemCount === 0);
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', itemCount === 0);

        const emptyState = document.querySelector('.drawer__inner-empty');
        if (emptyState) emptyState.style.display = itemCount === 0 ? '' : 'none';
      }

      if (sections['cart-icon-bubble']) {
        const bubbleDoc = this.parseSection(sections['cart-icon-bubble']);
        const newBubble = bubbleDoc.querySelector('.shopify-section') || bubbleDoc.getElementById('cart-icon-bubble');
        const currentBubble = document.getElementById('cart-icon-bubble');
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      }
    }
  };

  // ============================================================================
  // CART DRAWER UPSELLS
  // ============================================================================

  const CartDrawerUpsells = {
    container: null,
    cartDrawer: null,

    /**
     * Initialize upsells in cart drawer
     * @param {HTMLElement} container - Container element with upsells
     */
    init(container) {
      if (!container) return;
      this.container = container;
      this.cartDrawer = document.querySelector('cart-drawer');

      // Bind toggle events
      this.bindToggleEvents();

      // Sync initial state when drawer opens
      if (this.cartDrawer) {
        this.cartDrawer.addEventListener('transitionend', () => {
          if (this.cartDrawer.classList.contains('active')) {
            this.syncToggles();
          }
        }, { once: false });
      }

      // Listen to cart updates
      document.addEventListener(EVENTS.cartUpdated, (e) => {
        if (e.detail && e.detail.cart) {
          this.syncToggles(e.detail.cart);
        }
      });
    },

    /**
     * Bind event listeners for toggle switches
     */
    bindToggleEvents() {
      if (!this.container) return;

      this.container.addEventListener('change', async (e) => {
        const toggle = e.target.closest(SELECTORS.upsellToggle);
        if (!toggle) return;

        const row = toggle.closest(SELECTORS.upsellRow);
        if (!row) return;

        if (toggle.disabled) {
          toggle.checked = !toggle.checked;
          return;
        }

        toggle.disabled = true;

        try {
          if (toggle.checked) {
            await this.addUpsell(row);
          } else {
            await this.removeUpsell(row);
          }
        } catch (error) {
          console.error('Upsell toggle error:', error);
          toggle.checked = !toggle.checked;
        } finally {
          toggle.disabled = false;
        }
      });
    },

    /**
     * Sync toggle states with current cart contents
     * @param {Object} cart - Optional cart object (will fetch if not provided)
     */
    async syncToggles(cart = null) {
      if (!this.container) return;

      // Fetch cart if not provided
      if (!cart) {
        try {
          const response = await fetch('/cart.js');
          if (!response.ok) return;
          cart = await response.json();
        } catch (error) {
          console.error('Failed to fetch cart for sync:', error);
          return;
        }
      }

      // Map variant IDs to line keys
      const variantToLineKey = {};
      if (cart.items && Array.isArray(cart.items)) {
        cart.items.forEach(item => {
          if (item.variant_id && !variantToLineKey[item.variant_id]) {
            variantToLineKey[item.variant_id] = item.key || item.id;
          }
        });
      }

      // Update each upsell row
      const rows = this.container.querySelectorAll(SELECTORS.upsellRow);
      rows.forEach(row => {
        const variantId = parseInt(row.dataset.variantId, 10);
        const toggle = row.querySelector(SELECTORS.upsellToggle);
        if (!toggle) return;

        const isInCart = variantId in variantToLineKey;
        toggle.checked = isInCart;
        
        // Store line key for removal
        if (isInCart) {
          row.dataset.lineKey = variantToLineKey[variantId];
        } else {
          delete row.dataset.lineKey;
        }
      });
    },

    /**
     * Add upsell item to cart
     * @param {HTMLElement} row - Upsell row element
     */
    async addUpsell(row) {
      const variantId = parseInt(row.dataset.variantId, 10);
      const quantity = parseInt(row.dataset.qty || '1', 10);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      if (this.cartDrawer) {
        const sectionsToRender = this.cartDrawer.getSectionsToRender();
        const sectionKeys = sectionsToRender.map((section) => section.section || section.id);
        formData.append('sections', sectionKeys.join(','));
        formData.append('sections_url', window.location.pathname);
        this.cartDrawer.setActiveElement(document.activeElement);
      }

      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || 'Failed to add to cart');
      }

      // Update cart drawer without full re-render
      if (this.cartDrawer && data.sections) {
        CartDrawerPartialUpdate.apply(data.sections, data.item_count);
      }

      // Dispatch event
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent(EVENTS.cartUpdated, { detail: { cart } }));
    },

    /**
     * Remove upsell item from cart
     * @param {HTMLElement} row - Upsell row element
     */
    async removeUpsell(row) {
      const lineKey = row.dataset.lineKey;
      if (!lineKey) {
        // Try to find line key from cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const variantId = parseInt(row.dataset.variantId, 10);
        
        const cartItem = cart.items.find(item => item.variant_id === variantId);
        if (!cartItem) return;

        row.dataset.lineKey = cartItem.key || cartItem.id;
      }

      const body = JSON.stringify({
        id: row.dataset.lineKey,
        quantity: 0,
        sections: this.cartDrawer ? this.cartDrawer.getSectionsToRender().map((section) => section.section || section.id).join(',') : '',
        sections_url: window.location.pathname
      });

      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body
      });

      const parsedState = await response.json();

      if (parsedState.errors) {
        throw new Error(parsedState.errors || 'Failed to remove from cart');
      }

      // Update cart drawer without full re-render
      if (this.cartDrawer && parsedState.sections) {
        CartDrawerPartialUpdate.apply(parsedState.sections, parsedState.item_count);
      }

      // Dispatch event
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent(EVENTS.cartUpdated, { detail: { cart } }));
    }
  };

  // ============================================================================
  // COLLECTION UPSELLS (CART DRAWER)
  // ============================================================================

  const CollectionUpsells = {
    cartDrawer: null,

    init() {
      this.cartDrawer = document.querySelector('cart-drawer');
      if (!this.cartDrawer || this.cartDrawer.dataset.collectionUpsellsBound === 'true') return;

      this.cartDrawer.dataset.collectionUpsellsBound = 'true';
      this.cartDrawer.addEventListener('click', this.onClick.bind(this));
    },

    async onClick(event) {
      const button = event.target.closest(SELECTORS.collectionUpsellAtc);
      if (!button || button.disabled) return;

      const variantId = parseInt(button.dataset.variantId, 10);
      if (!variantId) return;

      event.preventDefault();

      const spinner = button.querySelector('.loading-overlay__spinner');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');

      try {
        await this.addToCart(variantId);
      } catch (error) {
        console.error('Collection upsell add error:', error);
      } finally {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
      }
    },

    async addToCart(variantId) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      if (this.cartDrawer) {
        const sectionsToRender = this.cartDrawer.getSectionsToRender();
        const sectionKeys = sectionsToRender.map((section) => section.section || section.id);
        formData.append('sections', sectionKeys.join(','));
        formData.append('sections_url', window.location.pathname);
        this.cartDrawer.setActiveElement(document.activeElement);
      }

      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || 'Failed to add to cart');
      }

      if (this.cartDrawer && data.sections) {
        CartDrawerPartialUpdate.apply(data.sections, data.item_count);
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      FreeShippingBar.update(cart);
      document.dispatchEvent(new CustomEvent(EVENTS.cartUpdated, { detail: { cart } }));

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-upsell', productVariantId: variantId, skipDrawerItemsRefresh: true });
      }
    }
  };

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  window.PulseCart = {
    /**
     * Fetch current cart
     * @returns {Promise<Object>} Cart object
     */
    async fetchCart() {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    },

    /**
     * Add items to cart
     * @param {Array} items - Array of {id: variantId, quantity: number}
     * @returns {Promise<Object>} Response from cart add
     */
    async addItems(items) {
      // Use JSON format (standard Shopify method for multiple items)
      const bodyData = {
        items: items
      };

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const sectionsToRender = cartDrawer.getSectionsToRender();
        const sectionKeys = sectionsToRender.map((section) => section.section || section.id);
        if (sectionKeys.length > 0) {
          bodyData.sections = sectionKeys.join(',');
          bodyData.sections_url = window.location.pathname;
        }
      }

      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(bodyData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ status: 422, description: 'Failed to add items to cart' }));
        throw new Error(errorData.description || errorData.message || 'Failed to add items to cart');
      }

      return await response.json();
    },

    /**
     * Change line item quantity
     * @param {string} lineKey - Line item key
     * @param {number} quantity - New quantity (0 to remove)
     * @returns {Promise<Object>} Response from cart change
     */
    async changeLine(lineKey, quantity) {
      const cartDrawer = document.querySelector('cart-drawer');
      const bodyData = {
        id: lineKey,
        quantity: quantity
      };

      if (cartDrawer) {
        const sectionsToRender = cartDrawer.getSectionsToRender();
        const sectionKeys = sectionsToRender.map((section) => section.section || section.id);
        bodyData.sections = sectionKeys.join(',');
        bodyData.sections_url = window.location.pathname;
      }

      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify(bodyData)
      });

      return await response.json();
    },

    /**
     * Update free shipping progress bar
     * @param {Object} cart - Cart object
     */
    updateFreeShippingBar(cart) {
      FreeShippingBar.update(cart);
    },

    updateCartDrawerPartial(sections, itemCount) {
      CartDrawerPartialUpdate.apply(sections, itemCount);
    },

    /**
     * Sync upsell toggles with cart state
     * @param {HTMLElement} rootEl - Root element containing upsells
     * @param {Object} cart - Optional cart object
     */
    async syncUpsellToggles(rootEl, cart = null) {
      if (!rootEl) return;
      CartDrawerUpsells.container = rootEl;
      await CartDrawerUpsells.syncToggles(cart);
    },

    /**
     * Bind upsell toggle events
     * @param {HTMLElement} rootEl - Root element containing upsells
     */
    bindUpsellToggles(rootEl) {
      if (!rootEl) return;
      CartDrawerUpsells.container = rootEl;
      CartDrawerUpsells.bindToggleEvents();
    }
  };

  // ============================================================================
  // AUTO-INITIALIZATION
  // ============================================================================

  function init() {
    // Initialize free shipping bar
    const freeShip = document.querySelector(SELECTORS.freeShip);
    if (freeShip) {
      // Initial update if cart is available
      if (typeof window.cart !== 'undefined' && window.cart) {
        FreeShippingBar.update(window.cart);
      } else {
        // Fetch cart and update
        window.PulseCart.fetchCart().then(cart => {
          FreeShippingBar.update(cart);
        }).catch(() => {
          // Silently fail if cart fetch fails
        });
      }
    }

    // Initialize cart drawer upsells
    const upsellContainer = document.querySelector(SELECTORS.upsellContainer);
    if (upsellContainer) {
      CartDrawerUpsells.init(upsellContainer);
    }

    CollectionUpsells.init();

    // Listen to cart updates
    document.addEventListener(EVENTS.cartUpdated, (e) => {
      if (e.detail && e.detail.cart) {
        FreeShippingBar.update(e.detail.cart);
      }
    });

    // Also listen to existing cart events if they exist
    if (typeof window.publish !== 'undefined' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      document.addEventListener('cart:updated', () => {
        window.PulseCart.fetchCart().then(cart => {
          FreeShippingBar.update(cart);
          document.dispatchEvent(new CustomEvent(EVENTS.cartUpdated, { detail: { cart } }));
        });
      });
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on section load (for theme editor)
  document.addEventListener('shopify:section:load', () => {
    setTimeout(init, 100);
  });

})();

